import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';

const positionTooltip = ({ elem, elemPlacement, overlay, offset = 8 }) => {
    if (elem === null || overlay === null)
        return false;
    if (typeof elemPlacement !== 'string' || elemPlacement.trim() === '') {
        return false;
    }
    const rect = elem.getBoundingClientRect();
    const contentRect = overlay.getBoundingClientRect();
    // Reset styles
    overlay.style.top = '';
    overlay.style.left = '';
    overlay.style.right = '';
    overlay.style.bottom = '';
    overlay.style.transform = '';
    // Use fixed positioning for portal overlays
    overlay.style.position = 'fixed';
    // Detect if the trigger is a span with only text or a text node
    let isTextTrigger = false;
    if (elem.childNodes.length === 1 && elem.childNodes[0].nodeType === Node.TEXT_NODE) {
        isTextTrigger = true;
    }
    else if (elem.childNodes.length === 1 && elem.childNodes[0].nodeType === Node.ELEMENT_NODE) {
        const child = elem.childNodes[0];
        if (child.tagName === 'SPAN' && child.childNodes.length === 1 && child.childNodes[0].nodeType === Node.TEXT_NODE) {
            isTextTrigger = true;
        }
    }
    switch (true) {
        case elemPlacement.includes('right'):
            if (isTextTrigger) {
                // Align to the bottom of the trigger for text triggers
                overlay.style.top = `${rect.bottom - contentRect.height / 2}px`;
            }
            else {
                overlay.style.top = `${rect.top + rect.height / 2 - contentRect.height / 2}px`;
            }
            overlay.style.left = `${rect.right + offset}px`;
            if (elemPlacement.includes('start')) {
                overlay.style.top = `${rect.top}px`;
            }
            if (elemPlacement.includes('end')) {
                overlay.style.top = `${rect.bottom - contentRect.height}px`;
            }
            break;
        case elemPlacement.includes('left'):
            if (isTextTrigger) {
                overlay.style.top = `${rect.bottom - contentRect.height / 2}px`;
            }
            else {
                overlay.style.top = `${rect.top + rect.height / 2 - contentRect.height / 2}px`;
            }
            overlay.style.left = `${rect.left - contentRect.width - offset}px`;
            if (elemPlacement.includes('start')) {
                overlay.style.top = `${rect.top}px`;
            }
            if (elemPlacement.includes('end')) {
                overlay.style.top = `${rect.bottom - contentRect.height}px`;
            }
            break;
        case elemPlacement.includes('bottom'):
            overlay.style.top = `${rect.bottom + offset}px`;
            overlay.style.left = `${rect.left + rect.width / 2 - contentRect.width / 2}px`;
            if (elemPlacement.includes('start')) {
                overlay.style.left = `${rect.left}px`;
            }
            if (elemPlacement.includes('end')) {
                overlay.style.left = `${rect.right - contentRect.width}px`;
                overlay.style.right = 'initial';
            }
            break;
        case elemPlacement.includes('top'):
            overlay.style.top = `${rect.top - contentRect.height - offset}px`;
            overlay.style.left = `${rect.left + rect.width / 2 - contentRect.width / 2}px`;
            if (elemPlacement.includes('start')) {
                overlay.style.left = `${rect.left}px`;
            }
            if (elemPlacement.includes('end')) {
                overlay.style.left = `${rect.right - contentRect.width}px`;
                overlay.style.right = 'initial';
            }
            break;
    }
    return true;
};

const pdsTooltipCss = ".pds-tooltip{--tooltip-border-width-arrow-down:var(--tooltip-sizing-arrow) var(--tooltip-sizing-arrow) 0;--tooltip-border-width-arrow-left:var(--tooltip-sizing-arrow) var(--tooltip-sizing-arrow) var(--tooltip-sizing-arrow) 0;--tooltip-border-width-arrow-right:var(--tooltip-sizing-arrow) 0 var(--tooltip-sizing-arrow) var(--tooltip-sizing-arrow);--tooltip-border-width-arrow-up:0 var(--tooltip-sizing-arrow) var(--tooltip-sizing-arrow);--tooltip-sizing-arrow:6px;--tooltip-sizing-arrow-offset:14px;--tooltip-dimension-max-width:320px}.pds-tooltip__content{background-color:var(--pine-color-primary);border-radius:calc(var(--pine-dimension-xs) * 1.25);-webkit-box-shadow:var(--pine-box-shadow);box-shadow:var(--pine-box-shadow);color:var(--pine-color-text-primary);max-width:var(--tooltip-dimension-max-width);opacity:0;padding:var(--pine-dimension-xs) calc(var(--pine-dimension-md) / 2);position:absolute;visibility:hidden;width:-webkit-max-content;width:-moz-max-content;width:max-content}.pds-tooltip--is-open .pds-tooltip__content{opacity:1;visibility:visible;z-index:1}.pds-tooltip.pds-tooltip--has-html-content .pds-tooltip__content{width:auto}.pds-tooltip__content::after{border-color:transparent;border-right-color:transparent;border-style:solid;border-width:var(--tooltip-border-width-arrow-left);content:\"\";height:0;position:absolute;width:0}.pds-tooltip--right .pds-tooltip__content::after{border-inline-end-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-left);left:calc(var(--tooltip-sizing-arrow) * -1);top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%)}.pds-tooltip--right-end .pds-tooltip__content::after{border-inline-end-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-left);bottom:var(--tooltip-sizing-arrow-offset);left:calc(var(--tooltip-sizing-arrow) * -1);top:initial}.pds-tooltip--right-start .pds-tooltip__content::after{border-inline-end-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-left);left:calc(var(--tooltip-sizing-arrow) * -1);top:var(--tooltip-sizing-arrow-offset)}.pds-tooltip--top .pds-tooltip__content::after{border-block-start-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-down);bottom:calc(var(--tooltip-sizing-arrow) * -1);left:50%;top:initial;-webkit-transform:translateX(-50%);transform:translateX(-50%)}.pds-tooltip--top-start .pds-tooltip__content::after{border-block-start-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-down);bottom:calc(var(--tooltip-sizing-arrow) * -1);left:var(--tooltip-sizing-arrow-offset);top:initial}.pds-tooltip--top-end .pds-tooltip__content::after{border-block-start-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-down);bottom:calc(var(--tooltip-sizing-arrow) * -1);left:initial;right:var(--tooltip-sizing-arrow-offset);top:initial}.pds-tooltip--left .pds-tooltip__content::after{border-inline-start-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-right);left:initial;right:calc(var(--tooltip-sizing-arrow) * -1);top:50%;-webkit-transform:translateY(-50%);transform:translateY(-50%)}.pds-tooltip--left-end .pds-tooltip__content::after{border-inline-start-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-right);bottom:var(--tooltip-sizing-arrow-offset);left:initial;right:calc(var(--tooltip-sizing-arrow) * -1);top:initial}.pds-tooltip--left-start .pds-tooltip__content::after{border-inline-start-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-right);left:initial;right:calc(var(--tooltip-sizing-arrow) * -1);top:var(--tooltip-sizing-arrow-offset)}.pds-tooltip--bottom .pds-tooltip__content::after{border-block-end-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-up);left:50%;top:calc(var(--tooltip-sizing-arrow) * -1);-webkit-transform:translateX(-50%);transform:translateX(-50%)}.pds-tooltip--bottom-end .pds-tooltip__content::after{border-block-end-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-up);left:initial;right:var(--tooltip-sizing-arrow-offset);top:calc(var(--tooltip-sizing-arrow) * -1)}.pds-tooltip--bottom-start .pds-tooltip__content::after{border-block-end-color:var(--pine-color-primary);border-width:var(--tooltip-border-width-arrow-up);left:var(--tooltip-sizing-arrow-offset);top:calc(var(--tooltip-sizing-arrow) * -1)}.pds-tooltip--no-arrow .pds-tooltip__content::after{border-width:0}";

const PdsTooltip$1 = /*@__PURE__*/ proxyCustomElement(class PdsTooltip extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        /**
         * Internal state: true if the tooltip was opened by user interaction (hover/focus),
         * false if opened via the `opened` prop or currently closed.
         */
        this._isInteractiveOpen = false;
        this.portalEl = null;
        this.triggerEl = null;
        this.contentDiv = null;
        this.slotMutationObserver = null;
        this.overlayResizeObserver = null;
        this.currentPathname = '';
        this.pathnameCheckInterval = null;
        /**
         * Determines when the tooltip is open
         * @defaultValue false
         */
        this.isOpen = false;
        /**
         * Determines whether or not the tooltip has an arrow
         * @defaultValue true
         */
        this.hasArrow = true;
        /**
         * Enable this option when using the content slot
         * @defaultValue false
         */
        this.htmlContent = false;
        /**
         * Determines the preferred position of the tooltip
         * @defaultValue "right"
         */
        this.placement = 'right';
        /**
         * Sets the maximum width of the tooltip content
         * @defaultValue "352px"
         */
        this.maxWidth = '352px';
        /**
         * Determines whether or not the tooltip is visible
         * @defaultValue false
         */
        this.opened = false;
        this.handleHide = () => {
            if (this.opened && !this._isInteractiveOpen) {
                return;
            }
            this.hideTooltip();
            this._isInteractiveOpen = false;
        };
        this.handleShow = () => {
            if (this.opened && !this._isInteractiveOpen) {
                return;
            }
            this._isInteractiveOpen = true;
            this.showTooltip();
        };
        this.handlePageShow = () => {
            if (this.opened && !this._isInteractiveOpen) {
                return;
            }
            this.opened = false;
            this._isInteractiveOpen = false;
        };
        this.handleScroll = () => {
            if (this.opened) {
                if (!this._isInteractiveOpen) {
                    this.repositionPortal();
                }
                else {
                    this.hideTooltip();
                    this._isInteractiveOpen = false;
                }
            }
        };
        this.handleSpaNavigation = () => {
            if (this.opened && !this._isInteractiveOpen) {
                return;
            }
            this.hideTooltip();
            this._isInteractiveOpen = false;
        };
        this.checkPathnameChange = () => {
            if (window.location.pathname !== this.currentPathname) {
                this.currentPathname = window.location.pathname;
                this.handleSpaNavigation();
            }
        };
    }
    handleOpenToggle(newValue, oldValue) {
        if (newValue === false && oldValue === true) {
            this._isInteractiveOpen = false;
        }
    }
    componentWillLoad() {
        this._isInteractiveOpen = false;
    }
    componentDidLoad() {
        window.addEventListener('pageshow', this.handlePageShow);
        this.currentPathname = window.location.pathname;
        this.triggerEl = this.el.querySelector('.pds-tooltip__trigger');
        const contentSlotWrapper = this.el.querySelector('.pds-tooltip__content-slot-wrapper');
        if (contentSlotWrapper !== null) {
            this.slotMutationObserver = new MutationObserver(() => {
                if (this.opened && this.portalEl !== null) {
                    this.removePortal();
                    this.createPortal();
                }
            });
            this.slotMutationObserver.observe(contentSlotWrapper, { childList: true, subtree: false });
        }
        return () => {
            window.removeEventListener('pageshow', this.handlePageShow);
            if (this.slotMutationObserver !== null) {
                this.slotMutationObserver.disconnect();
            }
        };
    }
    componentDidRender() {
        if (this.opened && this.portalEl === null) {
            this.createPortal();
        }
        else if (!this.opened && this.portalEl !== null) {
            this.removePortal();
        }
    }
    /**
     * Shows the tooltip by enabling the opened property
     */
    async showTooltip() {
        this.opened = true;
    }
    /**
     * Hides the tooltip by disabling the opened property
     */
    async hideTooltip() {
        this.opened = false;
    }
    /**
     * Determines the most accurate HTML element to use as the anchor for positioning the tooltip.
     * If `htmlContent` is false, it attempts to find the actual element slotted as the trigger.
     * Otherwise, or if no specific element is found, it defaults to the span wrapper around the trigger slot.
     * This helps with precise alignment.
     */
    determinePositioningAnchor() {
        let positioningAnchor = this.triggerEl;
        if (this.triggerEl !== null) {
            const children = this.triggerEl.childNodes;
            for (let i = 0; i < children.length; i++) {
                const childNode = children[i];
                if (childNode.nodeType === Node.ELEMENT_NODE) {
                    positioningAnchor = childNode;
                    break; // Found the first element, use it as the anchor
                }
            }
        }
        // If no ELEMENT_NODE is found within this.triggerEl (e.g., if trigger is just text),
        // positioningAnchor will correctly remain this.triggerEl (the span).
        return positioningAnchor;
    }
    /**
     * Centralized method to calculate and apply the tooltip's position.
     * Uses the determined anchor element and the current content dimensions.
     */
    repositionPortal() {
        const anchor = this.determinePositioningAnchor();
        if (anchor !== null && this.contentDiv !== null) {
            positionTooltip({ elem: anchor, elemPlacement: this.placement, overlay: this.contentDiv });
            const placementParts = this.placement.split('-');
            const primaryPlacement = placementParts[0];
            const isCardinalCenterPlacement = placementParts.length === 1;
            if (isCardinalCenterPlacement) {
                const anchorRect = anchor.getBoundingClientRect();
                const overlayRect = this.contentDiv.getBoundingClientRect();
                if (primaryPlacement === 'left' || primaryPlacement === 'right') {
                    const currentOverlayTop = parseFloat(this.contentDiv.style.top || '0');
                    const anchorCenterY = anchorRect.top + (anchorRect.height / 2);
                    const overlayCenterY = overlayRect.top + (overlayRect.height / 2);
                    const adjustmentY = anchorCenterY - overlayCenterY;
                    if (Math.abs(adjustmentY) > 0.5) {
                        this.contentDiv.style.top = `${currentOverlayTop + adjustmentY}px`;
                    }
                }
                else if (primaryPlacement === 'top' || primaryPlacement === 'bottom') {
                    const currentOverlayLeft = parseFloat(this.contentDiv.style.left || '0');
                    const anchorCenterX = anchorRect.left + (anchorRect.width / 2);
                    const overlayCenterX = overlayRect.left + (overlayRect.width / 2);
                    const adjustmentX = anchorCenterX - overlayCenterX;
                    if (Math.abs(adjustmentX) > 0.5) {
                        this.contentDiv.style.left = `${currentOverlayLeft + adjustmentX}px`;
                    }
                }
            }
        }
    }
    createPortal() {
        if (this.portalEl !== null)
            return;
        this.portalEl = document.createElement('div');
        this.portalEl.className = `pds-tooltip pds-tooltip--${this.placement} ${this.htmlContent ? 'pds-tooltip--has-html-content' : ''} ${this.opened ? 'pds-tooltip--is-open' : ''} ${this.hasArrow ? '' : 'pds-tooltip--no-arrow'}`;
        this.portalEl.style.position = 'fixed';
        this.portalEl.style.zIndex = '9999';
        if (this.portalEl.id === '') {
            this.portalEl.id = this.componentId || this.el.id || `pds-tooltip-portal-${PdsTooltip.instanceCounter++}`;
        }
        if (this.portalEl.getAttribute('id') !== this.portalEl.id) {
            this.portalEl.setAttribute('id', this.portalEl.id);
        }
        this.portalEl.setAttribute('role', 'tooltip');
        this.portalEl.setAttribute('aria-hidden', this.opened ? 'false' : 'true');
        this.portalEl.setAttribute('aria-live', this.opened ? 'polite' : 'off');
        this.portalEl.style.maxWidth = this.maxWidth;
        this.contentDiv = document.createElement('div');
        this.contentDiv.className = 'pds-tooltip__content';
        this.contentDiv.setAttribute('aria-hidden', this.opened ? 'false' : 'true');
        this.contentDiv.setAttribute('aria-live', this.opened ? 'polite' : 'off');
        this.contentDiv.setAttribute('role', 'tooltip');
        this.contentDiv.style.maxWidth = this.maxWidth;
        const contentSlotWrapper = this.el.querySelector('.pds-tooltip__content-slot-wrapper');
        const slottedContentContainer = contentSlotWrapper === null || contentSlotWrapper === void 0 ? void 0 : contentSlotWrapper.querySelector('[slot="content"]');
        let hasSlottedContent = false;
        if (slottedContentContainer !== null) {
            const childrenToClone = Array.from(slottedContentContainer.childNodes);
            if (childrenToClone.length > 0) {
                const hasMeaningfulNode = childrenToClone.some(node => {
                    var _a;
                    return node.nodeType === Node.ELEMENT_NODE ||
                        (node.nodeType === Node.TEXT_NODE && ((_a = node.textContent) === null || _a === void 0 ? void 0 : _a.trim()) !== '');
                });
                if (hasMeaningfulNode) {
                    hasSlottedContent = true;
                    childrenToClone.forEach((node /*, index*/) => {
                        var _a;
                        if (node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && ((_a = node.textContent) === null || _a === void 0 ? void 0 : _a.trim()) !== '')) {
                            this.contentDiv.appendChild(node.cloneNode(true));
                        }
                    });
                }
            }
        }
        if (!hasSlottedContent) {
            if (this.content !== '') {
                this.contentDiv.textContent = this.content;
            }
        }
        this.portalEl.appendChild(this.contentDiv);
        document.body.appendChild(this.portalEl);
        this.repositionPortal();
        if (this.contentDiv !== null) {
            this.overlayResizeObserver = new ResizeObserver(() => {
                this.repositionPortal();
            });
            this.overlayResizeObserver.observe(this.contentDiv);
        }
        // Add global listeners when portal is created
        window.addEventListener('scroll', this.handleScroll, true);
        window.addEventListener('popstate', this.handleSpaNavigation, true);
        window.addEventListener('hashchange', this.handleSpaNavigation, true);
        // Start pathname change detection
        this.pathnameCheckInterval = setInterval(this.checkPathnameChange, 100);
        // Add ARIA attribute to trigger, now that portalEl and its ID are confirmed
        if (this.triggerEl !== null && this.portalEl.id !== '') {
            this.triggerEl.setAttribute('aria-describedby', this.portalEl.id);
        }
    }
    removePortal() {
        if (this.overlayResizeObserver !== null && this.contentDiv !== null) {
            this.overlayResizeObserver.unobserve(this.contentDiv);
            this.overlayResizeObserver = null;
        }
        // Stop pathname change detection
        if (this.pathnameCheckInterval !== null) {
            clearInterval(this.pathnameCheckInterval);
            this.pathnameCheckInterval = null;
        }
        if (this.portalEl !== null) {
            window.removeEventListener('scroll', this.handleScroll, true);
            window.removeEventListener('popstate', this.handleSpaNavigation, true);
            window.removeEventListener('hashchange', this.handleSpaNavigation, true);
            document.body.removeChild(this.portalEl);
            this.portalEl = null;
        }
        // Remove ARIA attribute from trigger
        if (this.triggerEl !== null) {
            this.triggerEl.removeAttribute('aria-describedby');
        }
        this.contentDiv = null;
    }
    render() {
        const hostId = this.componentId || undefined;
        return (h(Host, { key: '462d8c0b1b70de08c0a4f9fe0b3977fa3b4af461', id: hostId, class: { 'pds-tooltip--is-open': this.opened } }, h("span", { key: 'dfb735da6e04d95701bc223bc054414d781c0f4a', class: "pds-tooltip__trigger", onMouseEnter: this.handleShow, onMouseLeave: this.handleHide, onFocus: this.handleShow, onBlur: this.handleHide, ref: el => this.triggerEl = el }, h("slot", { key: 'f0e5970a1ba6d59045306b836468cd00a06b183f' })), h("div", { key: '212ec606b29bd7a64a2ea3b1b4fae9746361a712', class: "pds-tooltip__content-slot-wrapper", style: { display: 'none' } }, h("slot", { key: '035ea5b2dfec0ca7f1462c30053f8f455cb986f2', name: "content" }))));
    }
    get el() { return this; }
    static get watchers() { return {
        "opened": ["handleOpenToggle"]
    }; }
    static get style() { return pdsTooltipCss; }
}, [4, "pds-tooltip", {
        "content": [1],
        "componentId": [1, "component-id"],
        "hasArrow": [4, "has-arrow"],
        "htmlContent": [4, "html-content"],
        "placement": [513],
        "maxWidth": [1, "max-width"],
        "opened": [1540],
        "_isInteractiveOpen": [32],
        "isOpen": [32],
        "showTooltip": [64],
        "hideTooltip": [64]
    }, undefined, {
        "opened": ["handleOpenToggle"]
    }]);
PdsTooltip$1.instanceCounter = 0;
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["pds-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "pds-tooltip":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PdsTooltip$1);
            }
            break;
    } });
}

const PdsTooltip = PdsTooltip$1;
const defineCustomElement = defineCustomElement$1;

export { PdsTooltip, defineCustomElement };
//# sourceMappingURL=pds-tooltip.js.map

//# sourceMappingURL=pds-tooltip.js.map